'use client';

import { Box, Flex, HStack, Link as ChakraLink, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { createPortal } from 'react-dom';
import { AppShellRailContent, useAppShellRailHeaders } from './AppShellHeader';

const APP_SHELL_TOOLBAR_SLOT_ID = 'app-shell-toolbar-slot';
const RailToolbarContext = createContext(false);
const TAB_TRANSITION = 'background-color 150ms ease, box-shadow 150ms ease, color 150ms ease';

function routeReservesToolbar(
  pathname: string | null,
  searchParams: { get: (key: string) => string | null }
) {
  if (!pathname) return false;
  if (pathname === '/app/workspace') {
    const mode = searchParams.get('mode');
    return (
      mode === 'tasks' ||
      searchParams.get('new_project') === 'true' ||
      searchParams.get('onboarding_chat') === 'true'
    );
  }
  return pathname === '/app/settings';
}

function routeUsesPersistentProductNavigation(pathname: string | null) {
  if (!pathname) return false;
  return (
    /^\/app\/ai-search\//.test(pathname) ||
    /^\/app\/brands\/[^/]+\/(?:weekly-report|fan-outs)$/.test(pathname) ||
    pathname === '/app/visibility' ||
    pathname === '/app/rank' ||
    pathname === '/app/rank-tables' ||
    pathname === '/app/sentiment' ||
    pathname === '/app/weekly-report' ||
    pathname === '/app/citation-report' ||
    pathname === '/app/citation-report/charts' ||
    pathname === '/app/channel-manager' ||
    pathname === '/app/publishers' ||
    pathname === '/app/publishers/tracker' ||
    pathname === '/app/topics' ||
    pathname === '/app/prompts' ||
    /^\/app\/prompts\//.test(pathname) ||
    pathname === '/app/fan-outs' ||
    pathname === '/app/citations' ||
    pathname === '/app/answers' ||
    /^\/app\/answers\//.test(pathname)
  );
}

interface AppShellToolbarPortalProps {
  children?: ReactNode;
  navigation?: ReactNode;
  timeline?: ReactNode;
  filters?: ReactNode;
  actions?: ReactNode;
}

interface AppShellToolbarRegistration<T> {
  update: (nextValue: T) => void;
  unregister: () => void;
}

interface AppShellToolbarContextValue {
  registerToolbar: (
    toolbarProps: AppShellToolbarPortalProps
  ) => AppShellToolbarRegistration<AppShellToolbarPortalProps>;
  registerPersistentNavigation: (navigation: ReactNode) => AppShellToolbarRegistration<ReactNode>;
}

const AppShellToolbarContext = createContext<AppShellToolbarContextValue | null>(null);

function AppShellToolbarContent({
  children,
  navigation,
  timeline,
  filters,
  actions,
  rail = false,
}: AppShellToolbarPortalProps & { rail?: boolean }) {
  if (rail && !children) {
    return (
      <Flex data-section="appShellToolbarContent" direction="column" w="full" minW={0} gap="3">
        {actions ? <Box data-section="appShellToolbarActions" minW={0}>{actions}</Box> : null}
        {navigation ? <Box data-section="appShellToolbarNavigation" minW={0}>{navigation}</Box> : null}
        {filters ? <Flex data-section="appShellToolbarFilters" direction="column" minW={0}>{filters}</Flex> : null}
        {timeline ? <Box data-section="appShellToolbarTimeline" minW={0}>{timeline}</Box> : null}
      </Flex>
    );
  }
  if (children) {
    return (
      <Flex
        data-section="appShellToolbarContent"
        w="full"
        minW={0}
        align="center"
        justify="space-between"
        gap={{ base: 1.5, md: 3 }}
        flexWrap="wrap"
      >
        {children}
      </Flex>
    );
  }

  const hasTopRow = Boolean(navigation || actions || timeline);
  const hasFilterRow = Boolean(filters);
  const useCompactUtilityRow = !navigation && Boolean(filters) && Boolean(actions || timeline);

  if (useCompactUtilityRow) {
    return (
      <Flex
        data-section="appShellToolbarCompactUtilityRow"
        w="full"
        minW={0}
        align="center"
        justify="space-between"
        gap={{ base: 1.5, md: 3 }}
        flexWrap="nowrap"
        overflowX="auto"
        css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        <Flex data-section="appShellToolbarFilters" minW={0} flex="1 0 300px" justify="flex-start">
          {filters}
        </Flex>
        <Flex
          data-section="appShellToolbarRight"
          align="center"
          justify="flex-end"
          gap={{ base: 1.5, md: 2 }}
          flexShrink={0}
          minW={0}
          ml="auto"
        >
          {actions ? (
            <Flex data-section="appShellToolbarActions" align="center" justify="flex-end" minW={0}>
              {actions}
            </Flex>
          ) : null}
          {timeline ? (
            <Flex data-section="appShellToolbarTimeline" minW={0} flexShrink={0}>
              {timeline}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex data-section="appShellToolbarContent" direction="column" w="full" minW={0} gap="2.5">
      {hasTopRow ? (
        <Flex
          data-section="appShellToolbarPrimaryRow"
          align="center"
          justify="space-between"
          gap={{ base: 1.5, md: 3 }}
          flexWrap="wrap"
          minW={0}
        >
          {navigation ? (
            <Flex data-section="appShellToolbarNavigation" align="center" minW={0} flex="1 1 auto">
              {navigation}
            </Flex>
          ) : null}
          {actions || timeline ? (
            <Flex
              data-section="appShellToolbarRight"
              align="center"
              justify="flex-end"
              gap={{ base: 1.5, md: 2 }}
              flexShrink={0}
              minW={0}
              ml="auto"
            >
              {actions ? (
                <Flex
                  data-section="appShellToolbarActions"
                  align="center"
                  justify="flex-end"
                  minW={0}
                >
                  {actions}
                </Flex>
              ) : null}
              {timeline ? (
                <Flex data-section="appShellToolbarTimeline" minW={0} flexShrink={0}>
                  {timeline}
                </Flex>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      ) : null}

      {hasFilterRow ? (
        <Flex
          data-section="appShellToolbarFilterRow"
          align="center"
          gap={{ base: 1.5, md: 3 }}
          flexWrap="wrap"
          minW={0}
        >
          {filters ? (
            <Flex
              data-section="appShellToolbarFilters"
              minW={0}
              flex="1 1 auto"
              justify="flex-start"
            >
              {filters}
            </Flex>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
}

export function AppShellToolbarFrame({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { inRail, railEligible, toolbarHost } = useAppShellRailHeaders();
  const routeKey = `${pathname}?${searchParams.toString()}`;
  const nextToolbarIdRef = useRef(0);
  const nextNavigationIdRef = useRef(0);
  const [toolbarEntries, setToolbarEntries] = useState<Map<number, AppShellToolbarPortalProps & { routeKey: string }>>(
    () => new Map()
  );
  const [navigationEntries, setNavigationEntries] = useState<Map<number, ReactNode>>(
    () => new Map()
  );

  const registerToolbar = useCallback((toolbarProps: AppShellToolbarPortalProps) => {
    const id = nextToolbarIdRef.current;
    nextToolbarIdRef.current += 1;
    setToolbarEntries(entries => {
      const nextEntries = new Map(entries);
      nextEntries.set(id, { ...toolbarProps, routeKey });
      return nextEntries;
    });

    return {
      update: (nextProps: AppShellToolbarPortalProps) => {
        setToolbarEntries(entries => {
          if (!entries.has(id)) return entries;
          const current = entries.get(id);
          if (
            current === nextProps ||
            (current?.actions === nextProps.actions &&
              current?.children === nextProps.children &&
              current?.filters === nextProps.filters &&
              current?.navigation === nextProps.navigation &&
              current?.timeline === nextProps.timeline)
          ) {
            return entries;
          }
          const nextEntries = new Map(entries);
          nextEntries.set(id, { ...nextProps, routeKey });
          return nextEntries;
        });
      },
      unregister: () => {
        setToolbarEntries(entries => {
          if (!entries.has(id)) return entries;
          const nextEntries = new Map(entries);
          nextEntries.delete(id);
          return nextEntries;
        });
      },
    };
  }, [routeKey]);

  const registerPersistentNavigation = useCallback((navigation: ReactNode) => {
    const id = nextNavigationIdRef.current;
    nextNavigationIdRef.current += 1;
    setNavigationEntries(entries => {
      const nextEntries = new Map(entries);
      nextEntries.set(id, navigation);
      return nextEntries;
    });

    return {
      update: (nextNavigation: ReactNode) => {
        setNavigationEntries(entries => {
          if (!entries.has(id)) return entries;
          if (entries.get(id) === nextNavigation) return entries;
          const nextEntries = new Map(entries);
          nextEntries.set(id, nextNavigation);
          return nextEntries;
        });
      },
      unregister: () => {
        setNavigationEntries(entries => {
          if (!entries.has(id)) return entries;
          const nextEntries = new Map(entries);
          nextEntries.delete(id);
          return nextEntries;
        });
      },
    };
  }, []);

  const activeToolbar = useMemo<AppShellToolbarPortalProps | null>(() => {
    const entries = Array.from(toolbarEntries.values()).filter(entry => entry.routeKey === routeKey);
    return entries.length > 0 ? entries[entries.length - 1] : null;
  }, [toolbarEntries, routeKey]);

  const persistentNavigation = useMemo<ReactNode>(() => {
    const entries = Array.from(navigationEntries.values());
    return entries.length > 0 ? entries[entries.length - 1] : null;
  }, [navigationEntries]);

  const toolbarContent = useMemo<AppShellToolbarPortalProps | null>(() => {
    if (!activeToolbar && !persistentNavigation) return null;
    if (activeToolbar?.children) return activeToolbar;
    const resolvedToolbar = {
      ...(activeToolbar ?? {}),
      navigation: activeToolbar?.navigation ?? persistentNavigation,
    };
    if (routeUsesPersistentProductNavigation(pathname)) {
      resolvedToolbar.navigation = undefined;
    }
    return resolvedToolbar.navigation ||
      resolvedToolbar.timeline ||
      resolvedToolbar.filters ||
      resolvedToolbar.actions
      ? resolvedToolbar
      : null;
  }, [activeToolbar, pathname, persistentNavigation]);

  const toolbarVisible = Boolean(toolbarContent) || routeReservesToolbar(pathname, searchParams);
  const contextValue = useMemo(
    () => ({ registerToolbar, registerPersistentNavigation }),
    [registerPersistentNavigation, registerToolbar]
  );

  return (
    <AppShellToolbarContext.Provider value={contextValue}>
      <Flex data-section="appShellToolbarFrame" direction="column" flex="1" minH={0} minW={0}>
        <Box
          data-section="appShellToolbar"
          display={toolbarVisible ? { base: 'block', lg: railEligible ? 'none' : 'block' } : 'none'}
          flexShrink={0}
          borderBottomWidth="1px"
          borderColor="ui.border.subtle"
          bg="ui.surface.card"
        >
          <Flex
            id={APP_SHELL_TOOLBAR_SLOT_ID}
            data-section="appShellToolbarSlot"
            minH={{ base: '46px', md: '54px' }}
            w="full"
            minW={0}
            align="center"
            px={{ base: '3', md: '3.5' }}
            py={{ base: 1.5, md: 2 }}
          >
            {toolbarContent && !inRail ? <AppShellToolbarContent {...toolbarContent} /> : null}
          </Flex>
        </Box>

        {inRail && toolbarHost && toolbarContent
          ? createPortal(
              <AppShellRailContent>
                <RailToolbarContext.Provider value={true}><AppShellToolbarContent {...toolbarContent} rail /></RailToolbarContext.Provider>
              </AppShellRailContent>,
              toolbarHost
            )
          : null}
        {children}
      </Flex>
    </AppShellToolbarContext.Provider>
  );
}

export function AppShellToolbarPortal(props: AppShellToolbarPortalProps) {
  const context = useContext(AppShellToolbarContext);
  const registrationRef = useRef<AppShellToolbarRegistration<AppShellToolbarPortalProps> | null>(
    null
  );

  useLayoutEffect(() => {
    if (!context) return undefined;
    const registration = context.registerToolbar(props);
    registrationRef.current = registration;
    return () => {
      registration.unregister();
      registrationRef.current = null;
    };
  }, [context]);

  useLayoutEffect(() => {
    registrationRef.current?.update(props);
  }, [props.actions, props.children, props.filters, props.navigation, props.timeline]);

  return null;
}

export function AppShellToolbarPersistentNavigation({ children }: { children: ReactNode }) {
  const context = useContext(AppShellToolbarContext);
  const registrationRef = useRef<AppShellToolbarRegistration<ReactNode> | null>(null);

  useLayoutEffect(() => {
    if (!context) return undefined;
    const registration = context.registerPersistentNavigation(children);
    registrationRef.current = registration;
    return () => {
      registration.unregister();
      registrationRef.current = null;
    };
  }, [context]);

  useLayoutEffect(() => {
    registrationRef.current?.update(children);
  }, [children]);

  return null;
}

export function AppShellToolbarTabs({ children }: { children: ReactNode }) {
  return (
    <HStack
      data-section="appShellToolbarTabs"
      gap="1"
      bg="transparent"
      borderRadius="0"
      p="0"
      flexWrap="nowrap"
      minW={0}
      overflowX="auto"
      css={{
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {children}
    </HStack>
  );
}

export const APP_SHELL_TOOLBAR_TAB_PROPS = {
  h: '40px',
  px: { base: '2.5', xl: '3' },
  borderRadius: '12px',
  borderWidth: '0',
  bg: 'transparent',
  color: 'ui.text.secondary',
  fontSize: '0.875rem',
  fontWeight: '500',
  lineHeight: '1',
  whiteSpace: 'nowrap',
  transition: TAB_TRANSITION,
  _hover: {
    bg: 'ui.surface.input',
    color: 'ui.text.primary',
    textDecoration: 'none',
  },
  _focus: { outline: 'none', boxShadow: 'none' },
  _focusVisible: { outline: 'none', boxShadow: 'none' },
} as const;

export const APP_SHELL_TOOLBAR_TAB_ACTIVE_PROPS = {
  bg: 'ui.surface.input',
  color: 'ui.text.primary',
  fontWeight: '600',
  boxShadow: 'none',
} as const;

interface AppShellToolbarTabLinkProps {
  href: string;
  active?: boolean;
  icon: ReactNode;
  label: string;
  children: ReactNode;
}

export function AppShellToolbarTabLink({
  href,
  active = false,
  icon,
  label,
  children,
}: AppShellToolbarTabLinkProps) {
  const rail = useContext(RailToolbarContext);
  return (
    <ChakraLink
      as={NextLink}
      href={href}
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      title={label}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      textDecoration="none"
      minW={0}
      {...APP_SHELL_TOOLBAR_TAB_PROPS}
      {...(active ? APP_SHELL_TOOLBAR_TAB_ACTIVE_PROPS : {})}
      gap={rail ? '2.5' : undefined}
      flexShrink={0}
      minH={rail ? '36px' : undefined}
    >
      <Box as="span" display={rail ? 'inline-flex' : { base: 'inline-flex', xl: 'none' }} flexShrink={0} aria-hidden="true">
        {icon}
      </Box>
      <Box as="span" display={rail ? 'inline-flex' : { base: 'none', xl: 'inline-flex' }} flex={rail ? '1' : undefined} minW={0} alignItems="center">
        {typeof children === 'string' ? <Text as="span">{children}</Text> : children}
      </Box>
    </ChakraLink>
  );
}

interface AppShellToolbarTabButtonProps {
  active?: boolean;
  icon: ReactNode;
  label: string;
  children: ReactNode;
  onClick: () => void;
}

export function AppShellToolbarTabButton({
  active = false,
  icon,
  label,
  children,
  onClick,
}: AppShellToolbarTabButtonProps) {
  const rail = useContext(RailToolbarContext);
  return (
    <Box
      as="button"
      data-voice-tab
      aria-pressed={active}
      aria-label={label}
      title={label}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minW={0}
      cursor="pointer"
      onClick={onClick}
      {...APP_SHELL_TOOLBAR_TAB_PROPS}
      {...(active ? APP_SHELL_TOOLBAR_TAB_ACTIVE_PROPS : {})}
      gap={rail ? '2.5' : undefined}
      flexShrink={0}
      minH={rail ? '36px' : undefined}
    >
      <Box as="span" display={rail ? 'inline-flex' : { base: 'inline-flex', xl: 'none' }} flexShrink={0} aria-hidden="true">
        {icon}
      </Box>
      <Box as="span" display={rail ? 'inline-flex' : { base: 'none', xl: 'inline-flex' }} flex={rail ? '1' : undefined} minW={0} alignItems="center">
        {typeof children === 'string' ? <Text as="span">{children}</Text> : children}
      </Box>
    </Box>
  );
}
